// AppConfirmDialog.js
import React, {useState, useCallback, useContext, createContext, useRef, useEffect} from 'react';
import {
    Dialog, DialogActions, DialogContent, DialogTitle,
    Button
} from '@mui/material';
import { AppStyle } from '../value/AppConst';
import { NonCapButton } from './VzmStyled';

// Context to provide the confirmation dialog functions
const ConfirmDialogContext = createContext();

export const useConfirmDialog = () => {
    return useContext(ConfirmDialogContext);
};

export const AppConfirmDialogProvider = ({children}) => {
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState({title:'', content:[]});
    const [option, setOption] = useState({});
    //const [callback, setCallback] = useState(null);
    const [noBackdrop, setNoBackdrop] = useState(false);
    const resolveRef = useRef(null);

    /**
     * Open the confirmation dialog
     * @param {Object} message {title: 'title', content: ['line1', 'line2', ...], option: {yesLabel: '예', noLabel: '아니오', alert: false}}
     * @param {Function} callback Callback function to be executed when the user clicks 'Yes' or 'No'
     */
    const openConfirmDialog = useCallback((message) => {
        // setMessage(message);
        const option = message.option || {};
        const title = message.title || '알림';
        const content = typeof message.content === 'string' ? [message.content] : message.content;
        setMessage({title, content});
        setOption(option);
        setNoBackdrop(false);
        setOpen(true);

        return new Promise((resolve) => {
            resolveRef.current = resolve;
        });
    }, []);

    const onClickYesOrNo = (confirm) => {
        setOpen(false);

        if (resolveRef.current) {
            resolveRef.current(confirm);
            resolveRef.current = null;
        }
    };

    useEffect(() => {
        if(!open) return;

        const onKeyDown = (e) => {
            const key = e.key;
            const upper = typeof key === 'string' ? key.toUpperCase() : key;

            if(option.alert) {
                e.preventDefault();
                onClickYesOrNo(true);
                return;
            }

            if(key === 'Enter' || key === ' ' || key === 'Spacebar' || upper === 'Y') {
                e.preventDefault();
                onClickYesOrNo(true);
                return;
            }

            if(key === 'Escape' || upper === 'N') {
                e.preventDefault();
                onClickYesOrNo(false);
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, option.alert]);

    return(
        <ConfirmDialogContext.Provider value={openConfirmDialog}>
            {children}
            <Dialog open={open} onClose={()=>onClickYesOrNo(false)}>
                <DialogTitle style={{
                    backgroundColor: option.alert ? AppStyle.WarnRGB : AppStyle.PrimaryRGB,
                    color: '#fff'
                }}>{message.title}</DialogTitle>
                <DialogContent>
                    <ul>
                    {
                        message.content.map((line, idx) => {
                            return(
                                <li key={idx}>{line}</li>
                            );
                        })
                    }
                    {
                        noBackdrop ?
                        <li>아래 버튼 둘 중 하나를 클릭해 주세요.</li> : null
                    }
                    </ul>
                </DialogContent>
                <DialogActions>
                    {
                        option.alert ?
                        <Button onClick={()=>onClickYesOrNo(true)}>확인</Button> :
                        <>
                            <NonCapButton
                                color={AppStyle.mui.secondary}
                                variant={AppStyle.mui.contained}
                                onClick={()=>onClickYesOrNo(true)}
                            >
                                {option.yesLabel || '예'} (Enter/Space)
                            </NonCapButton>
                            <NonCapButton
                                color={AppStyle.mui.primary}
                                variant={AppStyle.mui.contained}
                                onClick={()=>onClickYesOrNo(false)}
                                style={{marginLeft: 10}}
                            >
                                {option.noLabel || '아니오'} (Esc)
                            </NonCapButton>
                        </>
                    }
                </DialogActions>
            </Dialog>
        </ConfirmDialogContext.Provider>
    );
};